"use client";

import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

interface PageScrollValue {
  scrollTop: number;
  setScrollTop: (scrollTop: number) => void;
  scrollDirection: ScrollDirectionState;
  setScrollDirection: (scrollDirection: ScrollDirectionState) => void;
  sectionRefs: RefObject<HTMLElement>[];
  contentSectionRef: RefObject<HTMLElement> | null;
  tourSectionRef: RefObject<HTMLElement> | null;
  bioSectionRef: RefObject<HTMLElement> | null;
  merchSectionRef: RefObject<HTMLElement> | null;
  scrollToContent: () => void;
  scrollToTour: () => void;
  scrollToBio: () => void;
  scrollToTop: () => void;
  scrollToMerch: () => void;
  setActiveSectionRef: (ref: RefObject<HTMLElement>) => void;
  contentSectionActive: boolean;
  tourSectionActive: boolean;
  bioSectionActive: boolean;
}

const PageScrollContext = createContext<PageScrollValue>({
  scrollTop: 0,
  setScrollTop: () => {},
  scrollDirection: null,
  setScrollDirection: () => {},
  sectionRefs: [],
  contentSectionRef: null,
  tourSectionRef: null,
  bioSectionRef: null,
  merchSectionRef: null,
  scrollToContent: () => {},
  scrollToTour: () => {},
  scrollToBio: () => {},
  scrollToTop: () => {},
  scrollToMerch: () => {},
  setActiveSectionRef: () => {},
  contentSectionActive: false,
  tourSectionActive: false,
  bioSectionActive: false,
});

interface PageScrollProviderProps {
  children: ReactNode;
}

export type ScrollDirectionState = "up" | "down" | null;

export const PageScrollProvider = ({ children }: PageScrollProviderProps) => {
  const contentSectionRef = useRef<HTMLElement>(null);
  const tourSectionRef = useRef<HTMLElement>(null);
  const bioSectionRef = useRef<HTMLElement>(null);
  const merchSectionRef = useRef<HTMLElement>(null);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [scrollDirection, setScrollDirection] =
    useState<ScrollDirectionState>(null);
  const [activeSectionRef, setActiveSectionRef] =
    useState<RefObject<HTMLElement> | null>(null);

  const scrollToPageElement = (
    elementRef: RefObject<HTMLElement>,
    scrollOffset = 0
  ) => {
    if (elementRef && elementRef.current && window) {
      const offsetTop = window.scrollY ?? 0;
      const elementPosition =
        elementRef.current.getBoundingClientRect().top + offsetTop;
      const scrollPosition = elementPosition - scrollOffset;
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const scrollToPosition = (scrollPosition = 0) => {
    if (window) {
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const scrollToTop = () => scrollToPosition(0);

  const scrollToContent = useCallback(
    () => scrollToPageElement(contentSectionRef, 60),
    [contentSectionRef]
  );

  const scrollToTour = useCallback(
    () => scrollToPageElement(tourSectionRef, 60),
    [tourSectionRef]
  );

  const scrollToBio = useCallback(
    () => scrollToPageElement(bioSectionRef, 60),
    [bioSectionRef]
  );

  const scrollToMerch = useCallback(
    () => scrollToPageElement(merchSectionRef, 60),
    [merchSectionRef]
  );

  const sectionRefs = [contentSectionRef, tourSectionRef, bioSectionRef];

  const contentSectionActive = activeSectionRef === contentSectionRef;
  const tourSectionActive = activeSectionRef === tourSectionRef;
  const bioSectionActive = activeSectionRef === bioSectionRef;

  return (
    <PageScrollContext.Provider
      value={{
        scrollTop,
        setScrollTop,
        scrollDirection,
        setScrollDirection,
        sectionRefs,
        contentSectionRef,
        tourSectionRef,
        bioSectionRef,
        merchSectionRef,
        scrollToContent,
        scrollToTour,
        scrollToBio,
        scrollToTop,
        scrollToMerch,
        setActiveSectionRef,
        contentSectionActive,
        tourSectionActive,
        bioSectionActive,
      }}
    >
      {children}
    </PageScrollContext.Provider>
  );
};

export const usePageScroll = () => useContext(PageScrollContext);
